<template>
  <!-- Sidebar Toggle Button (only for sidebar layout) -->
  <button 
    v-if="navigationSettings?.layout === 'sidebar'"
    class="sidebar-toggle-btn"
    @click="toggleSidebar"
    :class="{ active: sidebarOpen }"
  >
    <i class="bi bi-list" v-if="!sidebarOpen"></i>
    <i class="bi bi-x-lg" v-else></i>
  </button>

  <!-- Sidebar Menu (only for sidebar layout) -->
  <div 
    v-if="navigationSettings?.layout === 'sidebar'"
    class="sidebar-menu"
    :class="{ active: sidebarOpen }"
    :style="{ 
      backgroundColor: navigationSettings?.backgroundColor || '#1a1a2e',
      color: navigationSettings?.textColor || '#ffffff'
    }"
  >
    <div class="sidebar-header">
      <div class="sidebar-logo">
        <img 
          :src="logoSrc" 
          alt="Logo" 
          class="logo-image"
          @error="handleLogoError"
        >
      </div>
      <button class="sidebar-close-btn" @click="closeSidebar">
        <i class="bi bi-x-lg"></i>
      </button>
    </div>
    
    <div class="sidebar-content">
      <ul class="sidebar-nav-list">
        <!-- Legacy: pages from settings -->
        <template v-if="menuMode === 'legacy'">
          <li v-for="page in menuPages" :key="page.id" class="sidebar-nav-item">
            <router-link 
              :to="page.path" 
              class="sidebar-nav-link" 
              :class="{ active: $route.path === page.path }"
              @click="closeSidebar"
            >
              <i :class="page.icon + ' me-2'"></i>{{ page.title }}
            </router-link>
          </li>
        </template>
        
        <!-- Menu Builder Items (nested menus) - Only in builder mode -->
        <template v-if="menuMode === 'builder' && menuBuilderItems.length > 0">
          <nav-menu-item-mobile
            v-for="item in menuBuilderItems"
            :key="item.id"
            :item="item"
            :is-logged-in="isLoggedIn"
            :is-admin="isAdmin"
            :menu-pages="menuPages"
            @close="closeSidebar"
          />
        </template>
        
        <!-- Custom Menu Items (legacy mode) -->
        <li v-if="menuMode === 'legacy'" v-for="item in customMenuItems" :key="item.title" class="sidebar-nav-item">
          <a 
            :href="item.url" 
            class="sidebar-nav-link"
            :target="item.openInNewTab ? '_blank' : '_self'"
            :rel="item.openInNewTab ? 'noopener noreferrer' : ''"
            @click="closeSidebar"
          >
            <i :class="item.icon + ' me-2'"></i>{{ item.title }}
          </a>
        </li>
        
        <div class="sidebar-divider"></div>
        
        <!-- Cart & Wishlist -->
        <li class="sidebar-nav-item" v-if="showCart">
          <router-link to="/cart" class="sidebar-nav-link" @click="closeSidebar">
            <i class="bi bi-cart3 me-2"></i>Καλάθι
            <span v-if="cartCount > 0" class="badge bg-primary ms-auto">{{ cartCount }}</span>
          </router-link>
        </li>
        <li class="sidebar-nav-item" v-if="showWishlist">
          <router-link to="/wishlist" class="sidebar-nav-link" @click="closeSidebar">
            <i class="bi bi-heart me-2"></i>Wishlist
            <span v-if="wishlistCount > 0" class="badge bg-danger ms-auto">{{ wishlistCount }}</span>
          </router-link>
        </li>
        
        <div class="sidebar-divider"></div>
        
        <!-- User Actions -->
        <li class="sidebar-nav-item" v-if="!isLoggedIn">
          <router-link to="/login" class="sidebar-nav-link" @click="closeSidebar">
            <i class="bi bi-person me-2"></i>Σύνδεση
          </router-link>
        </li>
        <template v-if="isLoggedIn">
          <li class="sidebar-nav-item" v-if="isAdmin">
            <router-link to="/admin" class="sidebar-nav-link" @click="closeSidebar">
              <i class="bi bi-speedometer2 me-2"></i>Admin Dashboard
            </router-link>
          </li>
          <li class="sidebar-nav-item">
            <router-link to="/profile" class="sidebar-nav-link" @click="closeSidebar">
              <i class="bi bi-person me-2"></i>Προφίλ
            </router-link>
          </li>
          <li class="sidebar-nav-item">
            <router-link to="/orders" class="sidebar-nav-link" @click="closeSidebar">
              <i class="bi bi-bag-check me-2"></i>Παραγγελίες
            </router-link>
          </li>
          <li class="sidebar-nav-item">
            <a href="#" class="sidebar-nav-link" @click.prevent="handleLogout">
              <i class="bi bi-box-arrow-right me-2"></i>Αποσύνδεση
            </a>
          </li>
        </template>
      </ul>
      
      <!-- Contact Info in Sidebar -->
      <div v-if="showContactInfo && (contactEmail || contactPhone)" class="sidebar-contact">
        <div class="sidebar-divider"></div>
        <div class="contact-info">
          <a v-if="contactEmail" :href="`mailto:${contactEmail}`" class="contact-link">
            <i class="bi bi-envelope-fill me-2"></i>{{ contactEmail }}
          </a>
          <a v-if="contactPhone" :href="`tel:${contactPhone}`" class="contact-link">
            <i class="bi bi-telephone-fill me-2"></i>{{ contactPhone }}
          </a>
        </div>
      </div>
    </div>
  </div>

  <!-- Sidebar Overlay -->
  <div 
    v-if="navigationSettings?.layout === 'sidebar' && sidebarOpen"
    class="sidebar-overlay"
    @click="closeSidebar"
  ></div>

  <nav class="navbar" :class="`navbar-layout-${navigationSettings?.layout || 'classic'}`">
    <!-- Contact Bar for Split Layout -->
    <div v-if="navigationSettings?.layout === 'split' && (showContactInfo || showSocialMedia)" class="contact-bar">
      <div class="container-fluid px-4">
        <div class="contact-left">
          <div v-if="contactEmail" class="contact-item">
            <i class="bi bi-envelope-fill me-2"></i>
            <a :href="`mailto:${contactEmail}`">{{ contactEmail }}</a>
          </div>
          <div v-if="contactPhone" class="contact-item">
            <i class="bi bi-telephone-fill me-2"></i>
            <a :href="`tel:${contactPhone}`">{{ contactPhone }}</a>
          </div>
        </div>
        
        <!-- Social Media Icons -->
        <div v-if="showSocialMedia" class="social-media-icons">
          <a v-if="socialLinks.facebook" :href="socialLinks.facebook" target="_blank" rel="noopener noreferrer" class="social-icon">
            <i class="bi bi-facebook"></i>
          </a>
          <a v-if="socialLinks.instagram" :href="socialLinks.instagram" target="_blank" rel="noopener noreferrer" class="social-icon">
            <i class="bi bi-instagram"></i>
          </a>
          <a v-if="socialLinks.twitter" :href="socialLinks.twitter" target="_blank" rel="noopener noreferrer" class="social-icon">
            <i class="bi bi-twitter-x"></i>
          </a>
          <a v-if="socialLinks.linkedin" :href="socialLinks.linkedin" target="_blank" rel="noopener noreferrer" class="social-icon">
            <i class="bi bi-linkedin"></i>
          </a>
          <a v-if="socialLinks.youtube" :href="socialLinks.youtube" target="_blank" rel="noopener noreferrer" class="social-icon">
            <i class="bi bi-youtube"></i>
          </a>
        </div>
      </div>
    </div>

    <div class="container-fluid px-4">
      <div class="nav-content" :class="`layout-${navigationSettings?.layout || 'classic'}`">
        <!-- Logo Section -->
        <div class="nav-logo">
          <router-link to="/" class="logo-link">
            <img 
              :src="logoSrc" 
              alt="Logo" 
              class="logo-image"
              @error="handleLogoError"
            >
          </router-link>
        </div>

        <!-- Desktop Menu -->
        <div class="nav-menu desktop-menu">
          <ul class="nav-list">
            <!-- Legacy: pages from settings -->
            <template v-if="menuMode === 'legacy'">
              <li v-for="page in menuPages" :key="page.id" class="nav-item">
                <router-link 
                  :to="page.path" 
                  class="nav-link" 
                  :class="{ active: $route.path === page.path }"
                >
                  <i :class="page.icon + ' me-1'"></i>{{ page.title }}
                </router-link>
              </li>
            </template>
            
            <!-- Menu Builder Items (nested menus) - Only in builder mode -->
            <template v-if="menuMode === 'builder' && menuBuilderItems.length > 0">
              <nav-menu-item
                v-for="item in menuBuilderItems"
                :key="item.id"
                :item="item"
                :is-logged-in="isLoggedIn"
                :is-admin="isAdmin"
                :menu-pages="menuPages"
              />
            </template>
            
            <!-- Custom Menu Items (legacy mode) -->
            <li v-if="menuMode === 'legacy'" v-for="item in customMenuItems" :key="item.title" class="nav-item">
              <a 
                :href="item.url" 
                class="nav-link"
                :target="item.openInNewTab ? '_blank' : '_self'"
                :rel="item.openInNewTab ? 'noopener noreferrer' : ''"
              >
                <i :class="item.icon + ' me-1'"></i>{{ item.title }}
              </a>
            </li>
          </ul>
        </div>

        <!-- Contact Info (if enabled) -->
        <div class="nav-contact desktop-menu" v-if="showContactInfo && (contactEmail || contactPhone)">
          <a v-if="contactEmail" :href="`mailto:${contactEmail}`" class="nav-contact-link" :title="contactEmail">
            <i class="bi bi-envelope-fill"></i>
          </a>
          <a v-if="contactPhone" :href="`tel:${contactPhone}`" class="nav-contact-link" :title="contactPhone">
            <i class="bi bi-telephone-fill"></i>
          </a>
        </div>

        <!-- Language Switcher -->
        <div class="nav-language desktop-menu">
          <LanguageSwitcher />
        </div>

        <!-- User Actions -->
        <div class="nav-actions desktop-menu">
          <ul class="nav-list">
            <!-- Cart Icon (only if showCart is enabled) -->
            <li class="nav-item" v-if="showCart">
              <router-link to="/cart" class="nav-link cart-icon-link">
                <i class="bi bi-cart3"></i>
                <span v-if="cartCount > 0" class="cart-count">{{ cartCount }}</span>
              </router-link>
            </li>
            
            <!-- Wishlist Icon (only if showWishlist is enabled) -->
            <li class="nav-item" v-if="showWishlist">
              <router-link to="/wishlist" class="nav-link wishlist-icon-link">
                <i class="bi bi-heart"></i>
                <span v-if="wishlistCount > 0" class="wishlist-count">{{ wishlistCount }}</span>
              </router-link>
            </li>
            
            <!-- Login/User -->
            <li class="nav-item" v-if="!isLoggedIn">
              <router-link to="/login" class="nav-link">
                <i class="bi bi-person me-1"></i>Σύνδεση
              </router-link>
            </li>
            <li class="nav-item dropdown" v-if="isLoggedIn">
              <a href="#" class="nav-link nav-submenu-toggle" @click.prevent="toggleUserDropdown">
                <i class="bi bi-person-circle me-1"></i>{{ user?.name || 'User' }}
                <i class="bi bi-chevron-down ms-1"></i>
              </a>
              <div class="dropdown-menu" :class="{ show: userDropdownOpen }">
                <router-link v-if="isAdmin" to="/admin" class="dropdown-item">
                  <i class="bi bi-speedometer2 me-2"></i>Admin Dashboard
                </router-link>
                <router-link to="/profile" class="dropdown-item">
                  <i class="bi bi-person me-2"></i>Προφίλ
                </router-link>
                <router-link to="/orders" class="dropdown-item">
                  <i class="bi bi-bag-check me-2"></i>Παραγγελίες
                </router-link>
                <div class="dropdown-divider"></div>
                <a href="#" class="dropdown-item" @click.prevent="handleLogout">
                  <i class="bi bi-box-arrow-right me-2"></i>Αποσύνδεση
                </a>
              </div>
            </li>
          </ul>
        </div>

        <!-- Mobile Menu Toggle -->
        <div class="mobile-actions">
          <div class="mobile-menu-toggle" @click="toggleMobileMenu">
            <i v-if="navigationSettings?.mobileBurgerIcon" :class="navigationSettings.mobileBurgerIcon" class="burger-icon"></i>
            <div v-else class="hamburger" :class="{ active: mobileMenuOpen }">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </nav>

  <!-- Mobile Menu Overlay (outside navbar) -->
  <div 
    v-if="mobileMenuOpen" 
    class="mobile-menu-overlay" 
    :class="{ 'blur-bg': navigationSettings?.mobileBlurBackground }"
    :style="{ 
      opacity: (navigationSettings?.mobileOverlayOpacity || 50) / 100,
      animationDuration: getAnimationSpeed()
    }"
    @click="closeMobileMenu"
  ></div>

  <!-- Mobile Menu (outside navbar) -->
  <div 
    class="mobile-menu" 
    :class="[
      { active: mobileMenuOpen },
      `mobile-menu-${navigationSettings?.mobileMenuStyle || 'slide-right'}`
    ]"
    :style="{ 
      backgroundColor: navigationSettings?.mobileMenuBackground || '#1a1a2e',
      color: navigationSettings?.mobileMenuTextColor || '#ffffff',
      animationDuration: getAnimationSpeed()
    }"
  >
    <ul class="mobile-nav-list">
      <!-- Legacy: pages from settings -->
      <template v-if="menuMode === 'legacy'">
        <li v-for="page in menuPages" :key="page.id" class="mobile-nav-item">
          <router-link 
            :to="page.path" 
            class="mobile-nav-link" 
            @click="closeMobileMenu"
          >
            <i :class="page.icon + ' me-2'"></i>{{ page.title }}
          </router-link>
        </li>
      </template>
      
      <!-- Menu Builder Items (nested menus) - Only in builder mode -->
      <template v-if="menuMode === 'builder' && menuBuilderItems.length > 0">
        <nav-menu-item-mobile
          v-for="item in menuBuilderItems"
          :key="item.id"
          :item="item"
          :is-logged-in="isLoggedIn"
          :is-admin="isAdmin"
          :menu-pages="menuPages"
          @close="closeMobileMenu"
        />
      </template>
      
      <!-- Custom Menu Items (legacy mode) -->
      <li v-if="menuMode === 'legacy'" v-for="item in customMenuItems" :key="item.title" class="mobile-nav-item">
        <a 
          :href="item.url" 
          class="mobile-nav-link"
          :target="item.openInNewTab ? '_blank' : '_self'"
          :rel="item.openInNewTab ? 'noopener noreferrer' : ''"
          @click="closeMobileMenu"
        >
          <i :class="item.icon + ' me-2'"></i>{{ item.title }}
        </a>
      </li>
      
      <div class="mobile-divider"></div>
      <li class="mobile-nav-item" v-if="!isLoggedIn">
        <router-link to="/login" class="mobile-nav-link" @click="closeMobileMenu">
          <i class="bi bi-person me-2"></i>Σύνδεση
        </router-link>
      </li>
      <li class="mobile-nav-item" v-if="isLoggedIn && isAdmin">
        <router-link to="/admin" class="mobile-nav-link" @click="closeMobileMenu">
          <i class="bi bi-speedometer2 me-2"></i>Admin Dashboard
        </router-link>
      </li>
      <li class="mobile-nav-item" v-if="isLoggedIn">
        <router-link to="/profile" class="mobile-nav-link" @click="closeMobileMenu">
          <i class="bi bi-person me-2"></i>Προφίλ
        </router-link>
      </li>
      <li class="mobile-nav-item" v-if="isLoggedIn">
        <router-link to="/orders" class="mobile-nav-link" @click="closeMobileMenu">
          <i class="bi bi-bag-check me-2"></i>Παραγγελίες
        </router-link>
      </li>
      <li class="mobile-nav-item" v-if="isLoggedIn">
        <a href="#" class="mobile-nav-link" @click.prevent="handleLogout">
          <i class="bi bi-box-arrow-right me-2"></i>Αποσύνδεση
        </a>
      </li>

      <!-- Contact Info inside mobile menu -->
      <template v-if="showContactInfo && (contactEmail || contactPhone)">
        <div class="mobile-divider"></div>
        <li class="mobile-nav-item" v-if="contactPhone">
          <a :href="`tel:${contactPhone}`" class="mobile-nav-link" @click="closeMobileMenu">
            <i class="bi bi-telephone-fill me-2"></i>{{ contactPhone }}
          </a>
        </li>
        <li class="mobile-nav-item" v-if="contactEmail">
          <a :href="`mailto:${contactEmail}`" class="mobile-nav-link" @click="closeMobileMenu">
            <i class="bi bi-envelope-fill me-2"></i>{{ contactEmail }}
          </a>
        </li>
      </template>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { eventBus } from '@/services/eventBus'
import { useWishlistStore } from '@/stores/wishlist'
import { useCartStore } from '@/stores/cart'
import { useUserStore } from '@/stores/user'
import { useSiteStyle } from '@/composables/useSiteStyle'
import { settingsService } from '@/services/api'
import { API_BASE_URL } from '@/utils/config'
import { authFetch } from '@/utils/authFetch'
import NavMenuItem from './NavMenuItem.vue'
import NavMenuItemMobile from './NavMenuItemMobile.vue'
import type { MenuItemData } from '@/components/admin/MenuBuilder.vue'
import { enrichMenuItems, fetchMenuLookupCategories } from '@/utils/menuBuilderHelpers'

const router = useRouter()

const IMAGE_BASE_URL = '/backend/api/image.php'

// Site style for conditional rendering
const { siteStyle, showCart, showWishlist, loadSiteStyle } = useSiteStyle()

// Menu state
const mobileMenuOpen = ref(false)
const userDropdownOpen = ref(false)
const sidebarOpen = ref(false)

// Logo state
const logoUrl = ref('logo.png') // Default logo

// Define types for navigation
interface NavigationSettings {
  backgroundColor?: string
  textColor?: string
  style?: string
  position?: string
  layout?: string
  showContactInfo?: boolean
  mobileMenuStyle?: string
  mobileAnimationSpeed?: string
  mobileMenuBackground?: string
  mobileMenuTextColor?: string
  mobileOverlayOpacity?: number
  mobileBlurBackground?: boolean
  mobileBurgerIcon?: string
  showSocialMedia?: boolean
}

interface MenuPage {
  id: string
  title: string
  path: string
  icon: string
  visible: boolean
  order: number
  displayAs?: string
  category?: string
}

interface CustomMenuItem {
  title: string
  url: string
  icon: string
  openInNewTab: boolean
  visible: boolean
  order?: number
}

// Navigation settings state
const navigationSettings = ref<NavigationSettings | null>(null)
const menuPages = ref<MenuPage[]>([])
const customMenuItems = ref<CustomMenuItem[]>([])
const menuBuilderItems = ref<MenuItemData[]>([])
const menuMode = ref<'legacy' | 'builder'>('legacy')
const showContactInfo = ref(false)
const showSocialMedia = ref(false)
const contactEmail = ref('')
const contactPhone = ref('')
const contactAddress = ref('')
const socialLinks = ref({
  facebook: '',
  instagram: '',
  twitter: '',
  linkedin: '',
  youtube: ''
})

// Store instances
const wishlistStore = useWishlistStore()
const cartStore = useCartStore()
const userStore = useUserStore()

// Cart and wishlist counts from stores
const cartCount = computed(() => cartStore.itemCount)
const wishlistCount = computed(() => wishlistStore.wishlistCount)

// Logo source computed - using dynamic logo from settings or fallback
// Served via img proxy at correct size (110px @2x = 220px) in WebP
const logoSrc = computed(() => {
  const raw = logoUrl.value && logoUrl.value !== 'logo.png'
    ? `/backend/uploads/logos/${logoUrl.value}`
    : `/backend/uploads/logos/logo.png`
  return `/backend/api/img.php?src=${encodeURIComponent(raw)}&h=110&q=85`
})

// Computed properties with debugging
const isLoggedIn = computed(() => {
  const result = userStore.isAuthenticated
  return result
})
const user = computed(() => {
  return userStore.currentUser
})

const isAdmin = computed(() => {
  return userStore.isAdmin
})

// Menu methods
const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value
  userDropdownOpen.value = false
}

const closeMobileMenu = () => {
  mobileMenuOpen.value = false
}

// Sidebar methods
const toggleSidebar = () => {
  sidebarOpen.value = !sidebarOpen.value
  // Close user dropdown if open
  userDropdownOpen.value = false
}

const closeSidebar = () => {
  sidebarOpen.value = false
}

const toggleUserDropdown = () => {
  userDropdownOpen.value = !userDropdownOpen.value
}

// Get animation speed based on settings
const getAnimationSpeed = () => {
  const speed = navigationSettings.value?.mobileAnimationSpeed || 'normal'
  switch (speed) {
    case 'fast': return '0.2s'
    case 'slow': return '0.5s'
    default: return '0.3s'
  }
}

const handleLogout = async () => {
  try {
    await userStore.logout()
    userDropdownOpen.value = false
    mobileMenuOpen.value = false
    router.push('/')
  } catch (error) {
  }
}

const handleLogoError = (event: Event) => {
  const img = event.target as HTMLImageElement
  img.src = '/logo.png'
}

// Load logo from settings
const loadLogo = async () => {
  try {
    const response = await settingsService.getSettings()
    if (response.success && response.settings && response.settings.logo_url) {
      logoUrl.value = response.settings.logo_url
    }
  } catch (error) {
  }
}

// Load contact info from settings
const loadContactInfo = async () => {
  try {
    const response = await settingsService.getSettings()
    
    if (response.success && response.settings) {
      contactEmail.value = response.settings.contact_email || ''
      contactPhone.value = response.settings.contact_phone || ''
      contactAddress.value = response.settings.contact_address || ''
    }
  } catch (error) {
    console.error('Error loading contact info:', error)
  }
}

// Load navigation settings and merge with dynamic pages
const loadNavigationSettings = async () => {
  try {
    
    // Load navigation settings, dynamic pages, and Instagram gallery in parallel
    const [navigationResponse, pagesResponse, instagramResponse] = await Promise.all([
      fetch(`${API_BASE_URL}/settings.php?action=get-navigation-settings`),
      fetch(`${API_BASE_URL}/pages.php?action=list&status=published&menu_only=true`),
      fetch(`${API_BASE_URL}/instagram.php?action=public-settings`)
    ])
    
    const navigationData = await navigationResponse.json()
    const pagesData = await pagesResponse.json()
    const instagramData = await instagramResponse.json()
    
    
    // Start with default navigation settings
    let allMenuPages: any[] = []
    
    if (navigationData.success && navigationData.data) {
      navigationSettings.value = navigationData.data.navigation || {}
      showContactInfo.value = navigationSettings.value?.showContactInfo || false
      showSocialMedia.value = navigationSettings.value?.showSocialMedia || false
      
      // Load contact info if enabled
      if (showContactInfo.value) {
        loadContactInfo()
      }
      
      // Load social media if enabled
      if (showSocialMedia.value) {
        loadSocialMediaLinks()
      }
      
      // Load configured pages from settings
      if (navigationData.data.pages) {
        const configuredPages = navigationData.data.pages
          .filter((page: any) => page.visible)
          .sort((a: any, b: any) => {
            // Ensure proper numeric sorting
            const aOrder = typeof a.order === 'number' ? a.order : parseInt(a.order) || 999
            const bOrder = typeof b.order === 'number' ? b.order : parseInt(b.order) || 999
            return aOrder - bOrder
          })
        
        allMenuPages = [...configuredPages]
      }
      
      // Load menu mode preference
      if (navigationData.data.menuMode) {
        menuMode.value = navigationData.data.menuMode
      }
      
      // Load menu builder items (new nested structure)
      if (navigationData.data.menuBuilderItems && navigationData.data.menuBuilderItems.length > 0) {
        menuBuilderItems.value = navigationData.data.menuBuilderItems
      }
      
      // Load custom menu items (legacy fallback)
      if (navigationData.data.customMenuItems) {
        const newCustomItems = navigationData.data.customMenuItems
          .filter((item: any) => item.visible)
          .sort((a: any, b: any) => (a.order || 999) - (b.order || 999))
        customMenuItems.value = newCustomItems
      }
    } else {
      // Use default pages if no navigation settings
      allMenuPages = [
        { id: 'home', title: 'Αρχική', path: '/', icon: 'bi bi-house', visible: true, order: 1 },
        { id: 'products', title: 'Προϊόντα', path: '/products', icon: 'bi bi-shop', visible: true, order: 2 },
        { id: 'tours', title: 'Εκδρομές', path: '/tours', icon: 'bi bi-airplane', visible: true, order: 3 },
        { id: 'about', title: 'Σχετικά', path: '/about', icon: 'bi bi-info-circle', visible: true, order: 4 },
        { id: 'faq', title: 'FAQ', path: '/faq', icon: 'bi bi-question-circle', visible: true, order: 5 },
        { id: 'contact', title: 'Επικοινωνία', path: '/contact', icon: 'bi bi-envelope', visible: true, order: 6 }
      ]
    }
    
    // Add dynamic pages from database
    if (pagesData.success && pagesData.data) {
      const dynamicPages = pagesData.data
        .filter((page: any) => page.show_in_menu)
        .map((page: any) => {
          // Check if this page already exists in configured pages from navigation settings
          const pageKey = `page-${page.id}`
          const existingPage = allMenuPages.find((p: any) => p.id === pageKey)
          
          if (existingPage) {
            // Update icon from fresh page data (overrides stale saved icon)
            if (page.menu_icon) {
              existingPage.icon = page.menu_icon
            }
            return null
          }
          
          return {
            id: pageKey,
            title: page.menu_title || page.title,
            path: `/${page.slug}`,
            icon: page.menu_icon || 'bi bi-file-text',
            visible: true,
            order: page.menu_order || 999,
            type: 'dynamic'
          }
        })
        .filter(Boolean) // Remove null entries
      
      // Merge with existing pages
      allMenuPages = [...allMenuPages, ...dynamicPages]
    }

    // Instagram Gallery menu item (when enabled in admin)
    if (instagramData.success && instagramData.gallery_enabled) {
      const galleryPath = instagramData.gallery_path || '/instagram-gallery'
      const galleryItem = {
        id: 'instagram-gallery',
        title: instagramData.gallery_menu_title || 'Gallery',
        path: galleryPath,
        icon: instagramData.gallery_menu_icon || 'bi bi-instagram',
        visible: true,
        order: 45,
        type: 'instagram'
      }
      const existingIdx = allMenuPages.findIndex((p: any) => p.id === 'instagram-gallery')
      if (existingIdx >= 0) {
        allMenuPages[existingIdx] = { ...allMenuPages[existingIdx], ...galleryItem }
      } else {
        allMenuPages.push(galleryItem)
      }
    } else {
      allMenuPages = allMenuPages.filter((p: any) => p.id !== 'instagram-gallery')
    }
    
    // Sort all pages by order with improved sorting
    allMenuPages.sort((a: any, b: any) => {
      const aOrder = typeof a.order === 'number' ? a.order : parseInt(a.order) || 999
      const bOrder = typeof b.order === 'number' ? b.order : parseInt(b.order) || 999
      return aOrder - bOrder
    })
    
    menuPages.value = allMenuPages

    if (menuBuilderItems.value.length > 0) {
      await loadSiteStyle()
      const menuCategories = await fetchMenuLookupCategories(siteStyle.value)
      menuBuilderItems.value = enrichMenuItems(menuBuilderItems.value, allMenuPages, menuCategories)
    }
    
    // Apply navbar styles if available
    if (navigationSettings.value) {
      applyNavigationStyles()
      setTimeout(() => {
        applyNavigationStyles()
      }, 100)
      
      // Toggle body class for sidebar layout
      if (navigationSettings.value.layout === 'sidebar') {
        document.body.classList.add('sidebar-layout-active')
      } else {
        document.body.classList.remove('sidebar-layout-active')
      }
    }

    // Load social media settings if showSocialMedia is enabled
    if (navigationSettings.value?.showSocialMedia) {
      loadSocialMediaLinks()
    }
    
  } catch (error) {
    // Fallback to default pages if settings fail to load
    setDefaultNavigationPages()
  }
}

// Load social media links from settings
const loadSocialMediaLinks = async () => {
  try {
    const response = await settingsService.getSettings()
    
    if (response.success && response.settings) {
      socialLinks.value = {
        facebook: response.settings.facebook_page_url || '',
        instagram: response.settings.instagram_url || '',
        twitter: response.settings.twitter_url || '',
        linkedin: response.settings.linkedin_url || '',
        youtube: response.settings.youtube_url || ''
      }
    }
  } catch (error) {
    console.error('Error loading social media links:', error)
  }
}

// Set default pages if navigation settings are not available
const setDefaultNavigationPages = () => {
  menuPages.value = [
    { id: 'home', title: 'Αρχική', path: '/', icon: 'bi bi-house', visible: true, order: 1 },
    { id: 'products', title: 'Προϊόντα', path: '/products', icon: 'bi bi-shop', visible: true, order: 2 },
    { id: 'tours', title: 'Εκδρομές', path: '/tours', icon: 'bi bi-airplane', visible: true, order: 3 },
    { id: 'about', title: 'Σχετικά', path: '/about', icon: 'bi bi-info-circle', visible: true, order: 4 },
    { id: 'faq', title: 'FAQ', path: '/faq', icon: 'bi bi-question-circle', visible: true, order: 5 },
    { id: 'contact', title: 'Επικοινωνία', path: '/contact', icon: 'bi bi-envelope', visible: true, order: 6 }
  ]
}

// Apply navigation styles from settings
const applyNavigationStyles = () => {
  const navbar = document.querySelector('.navbar') as HTMLElement
  if (navbar && navigationSettings.value) {
    
    const bgColor = navigationSettings.value.backgroundColor
    const textColor = navigationSettings.value.textColor

    // Apply background color with !important to override CSS
    if (bgColor) {
      navbar.style.setProperty('--nav-bg-color', bgColor)
      navbar.style.setProperty('--nav-dropdown-bg', bgColor)
      navbar.style.setProperty('--nav-dropdown-border', 'rgba(255, 255, 255, 0.15)')
      navbar.style.setProperty('--nav-dropdown-hover-bg', 'rgba(255, 255, 255, 0.12)')
      navbar.style.setProperty('background', bgColor, 'important')
      navbar.style.setProperty('background-color', bgColor, 'important')
      // Remove default gradient if any
      navbar.style.setProperty('background-image', 'none', 'important')
    }
    
    // Apply text color with !important to override CSS
    if (textColor) {
      navbar.style.setProperty('--nav-text-color', textColor)
      navbar.style.setProperty('color', textColor, 'important')
      
      // Apply to all nav links with !important
      const navLinks = navbar.querySelectorAll('.nav-link, .navbar-brand, .navbar-text')
      navLinks.forEach(link => {
        const element = link as HTMLElement
        element.style.setProperty('color', textColor, 'important')
      })
      
      // Apply to dropdown items and menu builder submenus
      const dropdownItems = navbar.querySelectorAll('.dropdown-item')
      dropdownItems.forEach(item => {
        const element = item as HTMLElement
        element.style.setProperty('color', textColor, 'important')
      })
    }

    if (bgColor) {
      const dropdownMenus = navbar.querySelectorAll('.nav-item.dropdown .dropdown-menu')
      dropdownMenus.forEach(menu => {
        const element = menu as HTMLElement
        element.style.setProperty('background', bgColor, 'important')
        element.style.setProperty('background-color', bgColor, 'important')
        if (textColor) {
          element.style.setProperty('color', textColor, 'important')
        }
      })
    }
    
    // Apply positioning classes
    if (navigationSettings.value.position) {
      navbar.classList.remove('position-static', 'position-fixed', 'sticky-top')
      
      switch (navigationSettings.value.position) {
        case 'fixed-top':
          navbar.classList.add('position-fixed', 'fixed-top')
          navbar.style.setProperty('z-index', '1030', 'important')
          break
        case 'sticky':
          navbar.classList.add('sticky-top')
          break
        default:
          navbar.classList.add('position-static')
      }
    }
    
    // Apply style variations
    if (navigationSettings.value.style) {
      switch (navigationSettings.value.style) {
        case 'transparent':
          navbar.style.setProperty('opacity', '0.9', 'important')
          navbar.style.setProperty('backdrop-filter', 'blur(10px)', 'important')
          break
        case 'shadow':
          navbar.style.setProperty('box-shadow', '0 2px 10px rgba(0,0,0,0.1)', 'important')
          break
        case 'gradient':
          if (navigationSettings.value.backgroundColor) {
            const gradientColor = adjustColor(navigationSettings.value.backgroundColor, -20)
            navbar.style.setProperty('background', 
              `linear-gradient(135deg, ${navigationSettings.value.backgroundColor}, ${gradientColor})`, 
              'important'
            )
          }
          break
        default:
          navbar.style.removeProperty('opacity')
          navbar.style.removeProperty('backdrop-filter')
          navbar.style.removeProperty('box-shadow')
      }
    }
    
  } else {
  }
}

// Helper function to adjust color brightness
const adjustColor = (color: string, amount: number): string => {
  const usePound = color[0] === '#'
  const col = usePound ? color.slice(1) : color
  
  const num = parseInt(col, 16)
  let r = (num >> 16) + amount
  let g = (num >> 8 & 0x00FF) + amount
  let b = (num & 0x0000FF) + amount
  
  r = r > 255 ? 255 : r < 0 ? 0 : r
  g = g > 255 ? 255 : g < 0 ? 0 : g
  b = b > 255 ? 255 : b < 0 ? 0 : b
  
  return (usePound ? '#' : '') + (r << 16 | g << 8 | b).toString(16).padStart(6, '0')
}

// Close dropdowns when clicking outside
const handleClickOutside = (event: Event) => {
  const target = event.target as HTMLElement
  if (!target.closest('.dropdown')) {
    userDropdownOpen.value = false
  }
  if (!target.closest('.mobile-menu-toggle') && !target.closest('.mobile-menu')) {
    mobileMenuOpen.value = false
  }
}

// Navigation settings update handler
const handleNavigationUpdate = (data?: any) => {
  loadNavigationSettings()
}

// Watch for changes in navigationSettings and apply styles immediately
watch(navigationSettings, (newSettings: any) => {
  if (newSettings) {
    setTimeout(() => {
      applyNavigationStyles()
      // Toggle body class for sidebar layout
      if (newSettings.layout === 'sidebar') {
        document.body.classList.add('sidebar-layout-active')
      } else {
        document.body.classList.remove('sidebar-layout-active')
      }
    }, 50)
  }
}, { deep: true })

// Window event handler for navigation settings
const windowNavigationHandler = (e: Event) => {
  const customEvent = e as CustomEvent
  handleNavigationUpdate(customEvent.detail)
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  
  // Listen for navigation settings updates using event bus
  eventBus.on('navigationSettingsUpdated', handleNavigationUpdate)
  
  // Also listen for window events as backup
  window.addEventListener('navigationSettingsUpdated', windowNavigationHandler)
  
  // Listen for footer settings updates to refresh social links
  window.addEventListener('footerSettingsUpdated', () => {
    if (navigationSettings.value?.showSocialMedia) {
      loadSocialMediaLinks()
    }
  })

  // Instagram gallery settings — refresh menu when toggled
  window.addEventListener('instagramSettingsUpdated', handleNavigationUpdate)
  
  // Initialize stores
  userStore.initializeAuth()
  cartStore.loadFromLocalStorage()
  wishlistStore.loadFromLocalStorage()
  
  // Load settings
  loadLogo()
  loadNavigationSettings()
  
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('navigationSettingsUpdated', windowNavigationHandler)
  window.removeEventListener('instagramSettingsUpdated', handleNavigationUpdate)
  eventBus.off('navigationSettingsUpdated', handleNavigationUpdate)
  // Clean up body class on unmount
  document.body.classList.remove('sidebar-layout-active')
})
</script>

<style scoped>
.navbar {
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  backdrop-filter: blur(10px);
  border-bottom: 2px solid rgba(212, 175, 55, 0.3);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 1100;
  padding: 0.75rem 0;
  overflow: visible;
}

.navbar .container-fluid,
.navbar .nav-content {
  overflow: visible;
}

.nav-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

/* ===== NAVBAR LAYOUTS ===== */

/* Classic Layout: Logo left, Menu center, Actions right */
.nav-content.layout-classic {
  justify-content: space-between;
}

.layout-classic .nav-logo {
  order: 1;
}

/* Layout-specific styles only apply on desktop */
@media (min-width: 769px) {
  .layout-classic .nav-menu {
    order: 2;
    flex: 1;
    display: flex;
    justify-content: center;
    margin: 0 2rem;
  }

  .layout-classic .nav-actions {
    order: 3;
  }
}

/* Centered Layout: Logo center, Menu below, Actions right */
.navbar-layout-centered .container-fluid {
  flex-direction: column;
  padding-top: 1rem;
}

.nav-content.layout-centered {
  flex-direction: column;
  text-align: center;
}

.layout-centered .nav-logo {
  order: 1;
  margin-bottom: 1rem;
}

@media (min-width: 769px) {
  .layout-centered .nav-menu {
    order: 2;
    width: 100%;
    display: flex;
    justify-content: center;
  }

  .layout-centered .nav-actions {
    order: 3;
    position: absolute;
    right: 2rem;
    top: 1.5rem;
  }
}

/* Side by Side Layout: Logo left, Menu next to logo, Actions right */
.nav-content.layout-side-by-side {
  justify-content: flex-start;
}

.layout-side-by-side .nav-logo {
  order: 1;
  margin-right: 2rem;
}

@media (min-width: 769px) {
  .layout-side-by-side .nav-menu {
    order: 2;
    flex: 1;
    display: flex;
    justify-content: flex-start;
  }

  .layout-side-by-side .nav-actions {
    order: 3;
    margin-left: auto;
  }
}

/* Minimal Layout: Logo left, Menu right, clean design */
.nav-content.layout-minimal {
  justify-content: space-between;
}

.layout-minimal .nav-logo {
  order: 1;
}

@media (min-width: 769px) {
  .layout-minimal .nav-menu {
    order: 2;
    flex: 1;
    display: flex;
    justify-content: flex-end;
    margin-right: 2rem;
  }

  .layout-minimal .nav-actions {
    order: 3;
  }
}

.layout-minimal .contact-info {
  display: none; /* Hide contact in minimal */
}

/* Split Layout: Logo left, Menu right, Contact bar on top */
.navbar-layout-split {
  position: relative;
}

.navbar-layout-split .contact-bar {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(10, 69, 99, 0.08) 100%);
  padding: 0.35rem 0;
  border-bottom: 1px solid rgba(0,0,0,0.05);
}

.navbar-layout-split .contact-bar .container-fluid {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  font-size: 0.8rem;
}

.navbar-layout-split .contact-bar .contact-left {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.navbar-layout-split .contact-bar .contact-item {
  display: flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 6px;
  transition: all 0.3s ease;
}

.navbar-layout-split .contact-bar .contact-item:hover {
  background: rgba(255, 255, 255, 0.8);
  transform: translateY(-1px);
}

.navbar-layout-split .contact-bar .contact-item a {
  color: var(--nav-text-color, #333);
  text-decoration: none;
  transition: color 0.3s ease;
  font-weight: 500;
}

.navbar-layout-split .contact-bar .contact-item a:hover {
  color: var(--nav-text-color, #333);
}

.navbar-layout-split .contact-bar .contact-item i {
  color: var(--nav-text-color, #333);
  font-size: 0.85rem;
  margin-right: 0.4rem;
}

/* Social Media Icons */
.social-media-icons {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.social-media-icons .social-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 50%;
  color: var(--nav-text-color, #333);
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 0.9rem;
}

.social-media-icons .social-icon:hover {
  background: rgba(255, 255, 255, 0.9);
  transform: translateY(-2px) scale(1.1);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.social-media-icons .social-icon i {
  color: var(--nav-text-color, #333);
}

.nav-content.layout-split {
  justify-content: space-between;
}

.layout-split .nav-logo {
  order: 1;
}

@media (min-width: 769px) {
  .layout-split .nav-menu {
    order: 2;
    flex: 1;
    display: flex;
    justify-content: flex-end;
    margin-right: 2rem;
  }

  .layout-split .nav-actions {
    order: 3;
  }
}

.layout-split .contact-info {
  display: none; /* Moved to top bar */
}

/* Logo */
.nav-logo {
  flex: 0 0 auto;
  order: 1;
}

.logo-link {
  display: block;
  transition: transform 0.2s ease;
}

.logo-link:hover {
  transform: scale(1.02);
}

.logo-image {
  height: 55px;
  width: auto;
  max-width: 220px;
  object-fit: contain;
}

/* Desktop Menu - Hidden by default (mobile-first) */
.desktop-menu {
  display: none;
}

/* Show desktop menu on larger screens */
@media (min-width: 769px) {
  .desktop-menu {
    display: flex;
  }
}

.nav-menu {
  flex: 1;
  justify-content: center;
  order: 2;
}

/* Nav menu display is controlled by .desktop-menu media query */

.nav-contact {
  flex: 0 0 auto;
  order: 2;
  gap: 0.5rem;
  margin-right: 1rem;
}

/* Nav contact display is controlled by .desktop-menu media query */

.nav-contact-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  color: #ecf0f1;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1.1rem;
}

.nav-contact-link:hover {
  background: rgba(212, 175, 55, 0.2);
  color: #d4af37;
  transform: translateY(-2px);
}

.nav-language {
  flex: 0 0 auto;
  order: 3;
  margin-right: 1rem;
}

.nav-actions {
  flex: 0 0 auto;
  order: 4;
}

.nav-list {
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 0.3rem;
}

.nav-item {
  position: relative;
}

.nav-link {
  position: relative;
  display: flex;
  align-items: center;
  padding: 0.5rem 0.8rem;
  color: #ecf0f1;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.8rem;
  border-radius: 6px;
  transition: all 0.2s ease;
  overflow: visible; /* Important for badges */
}

.nav-link::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(212, 175, 55, 0.1);
  border-radius: 6px;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.nav-link:hover::before {
  opacity: 1;
}

.nav-link:hover {
  color: #d4af37;
  transform: translateY(-1px);
}

.nav-link.active {
  color: #d4af37;
  background: rgba(212, 175, 55, 0.15);
}

.nav-link.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 30px;
  height: 2px;
  background: #d4af37;
  border-radius: 1px;
}

/* Cart and Wishlist Badges - Enhanced visibility */
.cart-icon-link,
.wishlist-icon-link {
  position: relative;
  font-size: 1.3rem;
}

.cart-icon-link i,
.wishlist-icon-link i {
  transition: all 0.3s ease;
}

.cart-icon-link:hover i {
  color: #d4af37;
  transform: scale(1.1);
}

.wishlist-icon-link:hover i {
  color: #dc3545;
  transform: scale(1.1);
}

.cart-count,
.wishlist-count {
  position: absolute;
  top: -10px;
  right: -10px;
  background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
  color: white;
  border: 2px solid #2c3e50;
  border-radius: 50%;
  font-size: 0.7rem;
  font-weight: bold;
  min-width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  box-shadow: 0 2px 6px rgba(231, 76, 60, 0.3);
  z-index: 10;
  transition: transform 0.2s ease;
}

.nav-link:hover .cart-count,
.nav-link:hover .wishlist-count {
  transform: scale(1.05);
}

/* Make sure the badge container has proper positioning */
.nav-link {
  position: relative;
  display: flex;
  align-items: center;
  padding: 0.5rem 0.8rem;
  color: #ecf0f1;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.8rem;
  border-radius: 6px;
  transition: all 0.3s ease;
  overflow: visible; /* Important for badges */
}

/* Dropdown */
.dropdown {
  position: relative;
}

.nav-submenu-toggle {
  cursor: pointer;
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  background: #2c3e50;
  border: 1px solid rgba(212, 175, 55, 0.3);
  border-radius: 12px;
  min-width: 200px;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: all 0.3s ease;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  z-index: 1200;
}

.dropdown-menu.show {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  color: #ecf0f1;
  text-decoration: none;
  transition: all 0.2s ease;
  border-radius: 6px;
  margin: 0.25rem;
}

.dropdown-item:hover {
  background: rgba(212, 175, 55, 0.1);
  color: #d4af37;
}

.dropdown-divider {
  height: 1px;
  background: rgba(212, 175, 55, 0.3);
  margin: 0.5rem 0;
}

/* Mobile Actions Container */
.mobile-actions {
  display: none;
  align-items: center;
  gap: 0.5rem;
  order: 4;
}

.mobile-menu-contact {
  display: flex;
  gap: 0.5rem;
}

/* Mobile Menu Toggle */
.mobile-menu-toggle {
  display: none;
  flex-direction: column;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: background 0.3s ease;
}

.mobile-menu-toggle:hover {
  background: rgba(212, 175, 55, 0.1);
}

.burger-icon {
  font-size: 1.5rem;
  color: #ecf0f1;
  transition: all 0.3s ease;
}

.burger-icon:hover {
  color: #d4af37;
}

.hamburger {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.hamburger span {
  width: 25px;
  height: 3px;
  background: #ecf0f1;
  border-radius: 2px;
  transition: all 0.3s ease;
}

.hamburger.active span:nth-child(1) {
  transform: rotate(45deg) translate(6px, 6px);
}

.hamburger.active span:nth-child(2) {
  opacity: 0;
}

.hamburger.active span:nth-child(3) {
  transform: rotate(-45deg) translate(6px, -6px);
}

/* Mobile Menu Overlay */
.mobile-menu-overlay {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 998;
  animation: fadeIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.mobile-menu-overlay.blur-bg {
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fadeOut {
  from { opacity: 1; }
  to { opacity: 0; }
}

/* Mobile Menu Base */
.mobile-menu {
  display: none;
  position: fixed;
  background: #2c3e50;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  z-index: 999;
  overflow-y: auto;
  transition: all 0.3s ease;
}

.mobile-menu.active {
  display: block;
}

/* Slide from Left */
.mobile-menu-slide-left {
  top: 0;
  left: 0;
  bottom: 0;
  width: 85%;
  max-width: 350px;
  transform: translateX(-100%) scale(0.95);
  opacity: 0;
  transition: transform 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55), opacity 0.5s ease;
}

.mobile-menu-slide-left.active {
  transform: translateX(0) scale(1);
  opacity: 1;
}

/* Slide from Right (Default) */
.mobile-menu-slide-right {
  top: 0;
  right: 0;
  bottom: 0;
  width: 85%;
  max-width: 350px;
  transform: translateX(100%) scale(0.95);
  opacity: 0;
  transition: transform 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55), opacity 0.5s ease;
}

.mobile-menu-slide-right.active {
  transform: translateX(0) scale(1);
  opacity: 1;
}

/* Dropdown from Top */
.mobile-menu-dropdown {
  top: 70px;
  left: 0;
  right: 0;
  border-radius: 0 0 16px 16px;
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  transform: translateY(-20px);
  transition: max-height 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55), 
              opacity 0.4s ease,
              transform 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.mobile-menu-dropdown.active {
  max-height: calc(100vh - 70px);
  opacity: 1;
  transform: translateY(0);
}

/* Sidebar Toggle Button (Fixed Position) */
.sidebar-toggle-btn {
  position: fixed;
  top: 20px;
  left: 20px;
  z-index: 10000;
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  transition: all 0.3s ease;
}

.sidebar-toggle-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.5);
}

.sidebar-toggle-btn.active {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
}

/* Sidebar Menu */
.sidebar-menu {
  position: fixed;
  top: 0;
  left: 0;
  width: 320px;
  height: 100vh;
  /* Background and color are set via inline styles from navigationSettings */
  z-index: 9999;
  transform: translateX(-100%);
  transition: transform 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.3);
  overflow-y: auto;
  overflow-x: hidden;
}

.sidebar-menu.active {
  transform: translateX(0);
}

/* Sidebar Header */
.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.sidebar-logo {
  flex: 1;
}

.sidebar-logo .logo-image {
  height: 45px;
  max-width: 150px;
  object-fit: contain;
}

.sidebar-close-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  width: 35px;
  height: 35px;
  border-radius: 8px;
  color: inherit;
  font-size: 1.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.sidebar-close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: rotate(90deg);
}

/* Sidebar Content */
.sidebar-content {
  padding: 1rem 0;
}

.sidebar-nav-list {
  list-style: none;
  margin: 0;
  padding: 0 1rem;
}

.sidebar-nav-item {
  margin-bottom: 0.5rem;
}

.sidebar-nav-link {
  display: flex;
  align-items: center;
  padding: 1rem 1.25rem;
  color: inherit;
  text-decoration: none;
  border-radius: 10px;
  transition: all 0.3s ease;
  font-weight: 500;
  font-size: 1.05rem;
  position: relative;
  overflow: hidden;
  opacity: 0.85;
}

.sidebar-nav-link::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: currentColor;
  transform: scaleY(0);
  transition: transform 0.3s ease;
}

.sidebar-nav-link:hover,
.sidebar-nav-link.active {
  background: rgba(255, 255, 255, 0.1);
  opacity: 1;
  transform: translateX(5px);
}

.sidebar-nav-link:hover::before,
.sidebar-nav-link.active::before {
  transform: scaleY(1);
}

.sidebar-nav-link i {
  font-size: 1.1rem;
  width: 24px;
}

.sidebar-nav-link .badge {
  margin-left: auto;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

/* Sidebar Divider */
.sidebar-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 1rem 1.5rem;
}

/* Sidebar Contact Info */
.sidebar-contact {
  margin-top: auto;
  padding: 1rem 1.5rem 1.5rem;
}

.sidebar-contact .contact-info {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.sidebar-contact .contact-link {
  display: flex;
  align-items: center;
  color: inherit;
  opacity: 0.7;
  text-decoration: none;
  font-size: 0.9rem;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.sidebar-contact .contact-link:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.1);
}

.sidebar-contact .contact-link i {
  color: inherit;
}

/* Sidebar Overlay */
.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 9998;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Hide navbar when sidebar layout is active */
.navbar-layout-sidebar {
  display: none !important;
}

/* Fullscreen Overlay */
.mobile-menu-fullscreen {
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  visibility: hidden;
  transform: scale(0.9);
  transition: opacity 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55), 
              visibility 0.4s,
              transform 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.mobile-menu-fullscreen.active {
  opacity: 1;
  visibility: visible;
  transform: scale(1);
}

/* Bottom Sheet */
.mobile-menu-bottom-sheet {
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 20px 20px 0 0;
  max-height: 85vh;
  transform: translateY(100%);
  transition: transform 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 2rem;
}

.mobile-menu-bottom-sheet.active {
  transform: translateY(0);
}

.mobile-nav-list {
  list-style: none;
  margin: 0;
  padding: 1rem 1rem 2rem 1rem;
  min-height: 100%;
}

.mobile-nav-item {
  margin-bottom: 0.15rem;
}

.mobile-nav-link {
  display: flex;
  align-items: center;
  padding: 0.55rem 1rem;
  color: #ecf0f1;
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.2s ease;
  font-weight: 500;
}

.mobile-nav-link:hover {
  background: rgba(212, 175, 55, 0.1);
  color: #d4af37;
  transform: translateX(3px);
}

.mobile-divider {
  height: 1px;
  background: rgba(212, 175, 55, 0.3);
  margin: 1rem 0;
}

/* Mobile Badge Styling */
.mobile-nav-link .ms-auto {
  background: #e74c3c;
  color: white;
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 0.75rem;
  font-weight: bold;
  min-width: 20px;
  text-align: center;
}

/* Responsive - Mobile */
@media (max-width: 768px) {
  .mobile-actions {
    display: flex;
  }
  
  .mobile-menu-toggle {
    display: flex;
  }
  
  .nav-content {
    gap: 1rem;
  }
  
  .logo-image {
    height: 45px;
  }

  /* Reset layouts for mobile */
  .nav-content.layout-centered,
  .nav-content.layout-side-by-side,
  .nav-content.layout-minimal,
  .nav-content.layout-split {
    flex-direction: row !important;
    justify-content: space-between !important;
  }

  .layout-centered .nav-logo,
  .layout-side-by-side .nav-logo,
  .layout-minimal .nav-logo,
  .layout-split .nav-logo {
    margin: 0 !important;
  }

  .layout-centered .nav-actions {
    position: static !important;
  }

  .navbar-layout-split .contact-bar {
    font-size: 0.8rem;
  }

  .navbar-layout-split .contact-bar .container-fluid {
    gap: 1rem;
  }
}

@media (max-width: 480px) {
  .navbar {
    padding: 0.5rem 0;
  }
  
  .logo-image {
    height: 38px;
  }
}
</style>

<!-- Global styles for fixed navbar compensation -->
<style>
body {
  padding-top: 80px; /* Add space for fixed navbar */
}

/* Remove padding when sidebar layout is active */
body.sidebar-layout-active {
  padding-top: 0 !important;
}

main, .main-content {
  padding-top: 0; /* Reset any existing padding */
}

/* Mobile specific fixes */
@media (max-width: 768px) {
  body {
    padding-top: 70px; /* Reduced padding for mobile */
  }
  
  body.sidebar-layout-active {
    padding-top: 0 !important;
  }
}

@media (max-width: 480px) {
  body {
    padding-top: 60px; /* Even smaller padding for smaller screens */
  }
  
  body.sidebar-layout-active {
    padding-top: 0 !important;
  }
}
</style>
